/**
 * Blog Post Page
 *
 * Leitor de um post do Blog (renderiza content_html sanitizado).
 */
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Loader2, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useBlogPost } from '@/hooks/use-learning';
import { resolveMediaUrl } from '@/lib/api';
import { sanitizeHtml } from '@/lib/sanitize';
import { coverPositionStyle } from '@/lib/cover';
import { formatBrasilia } from '@/lib/brasiliaDate';

export default function BlogPostPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { data: post, isLoading, isError } = useBlogPost(slug);

  const coverUrl = post?.cover_image_url;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" className="gap-2 -ml-2" onClick={() => navigate('/learn/blog')}>
        <ArrowLeft className="h-4 w-4" />
        Voltar para o Blog
      </Button>

      {(() => {
        if (isLoading) return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (isError || !post) return (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Artigo não encontrado</h3>
                <p className="text-muted-foreground">O artigo pode ter sido removido ou despublicado.</p>
              </CardContent>
            </Card>
        );
        return (
            <article className="space-y-6">
              <header className="space-y-3">
                {post.category && (
                  <Badge variant="secondary" className="w-fit">
                    {post.category}
                  </Badge>
                )}
                <h1 className="text-3xl font-bold">{post.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  {post.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatBrasilia(post.created_at, "dd 'de' MMMM 'de' yyyy")}
                    </span>
                  )}
                  {post.read_time_minutes ? (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {post.read_time_minutes} min de leitura
                    </span>
                  ) : null}
                </div>
              </header>

              {coverUrl && (
                <img
                  src={resolveMediaUrl(coverUrl)}
                  alt={post.title}
                  style={coverPositionStyle(post.cover_position)}
                  className="w-full rounded-lg border object-cover max-h-96"
                />
              )}

              {post.content_html ? (
                <div
                  className="prose prose-slate dark:prose-invert max-w-none [&_img]:rounded-lg [&_img]:border [&_img]:border-border [&_p:empty]:min-h-[1.5em]"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
                />
              ) : (
                post.excerpt && <p className="text-muted-foreground">{post.excerpt}</p>
              )}
            </article>
        );
      })()}
    </div>
  );
}
